/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : TestCaseComponentLinkDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 * ============================================================================
 */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Stack } from "@mui/material";
import { useDispatch } from "react-redux";
import { HandleApiActions } from "../../../redux/actions";
import testCasesActions from "../../../redux/actions/testCasesActions";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomMultiSelectV2 from "../../../components/customComponentsV2/common/multiselect/CustomMultiSelectV2";

function TestCaseComponentLinkDialog({
  open,
  testCasesToLink,
  selectedEEMain,
  selectedLegalMain,
  onClose,
  onSuccess,
  entityType = "2",
}) {
  const dispatch = useDispatch();

  const [selectedEEs, setSelectedEEs] = useState([]);
  const [selectedLegals, setSelectedLegals] = useState([]);

  useEffect(() => {
    if (open) {
      setSelectedEEs([]);
      setSelectedLegals([]);
    }
  }, [open]);

  const isComponentUnlinked = (tc) => {
    const val =
      tc?.is_component_linked ??
      tc?.targetContext?.is_component_linked ??
      tc?.raw?.targetContext?.is_component_linked ??
      tc?._ai?.targetContext?.is_component_linked;
    return val === false || String(val).toLowerCase() === "false";
  };

  const isLrUnlinked = (tc) => {
    const val =
      tc?.is_lr_linked ??
      tc?.targetContext?.is_lr_linked ??
      tc?.raw?.targetContext?.is_lr_linked ??
      tc?._ai?.targetContext?.is_lr_linked;
    return val === false || String(val).toLowerCase() === "false";
  };

  const hasSpecificUnlinked = (testCasesToLink || []).some(
    (tc) => isComponentUnlinked(tc) || isLrUnlinked(tc)
  );

  const showKeywordSelect = hasSpecificUnlinked
    ? (testCasesToLink || []).some((tc) => isComponentUnlinked(tc))
    : true;

  const showLegalSelect = hasSpecificUnlinked
    ? (testCasesToLink || []).some((tc) => isLrUnlinked(tc))
    : true;

  const isSubmitDisabled =
    (showKeywordSelect && selectedEEs.length === 0) ||
    (showLegalSelect && selectedLegals.length === 0);

  const eeOptions = (selectedEEMain || [])
    .flatMap((ee) => {
      if (Array.isArray(ee?.keywords) && ee.keywords.length > 0) {
        return ee.keywords.map((kw) => ({
          value: kw?.keyword_id ?? kw?.id ?? kw?.value ?? kw,
          label: kw?.keyword_name ?? kw?.name ?? kw?.label ?? String(kw),
        }));
      }
      return [
        {
          value:
            ee?.keyword_id || ee?.id || ee?.ee_id || ee?.swm_id || ee?.vs_id,
          label:
            ee?.keyword_name ||
            ee?.keyword ||
            ee?.name ||
            ee?.assembly_number ||
            ee?.description,
        },
      ];
    })
    .filter(
      (opt, index, self) =>
        opt.value !== undefined &&
        opt.value !== null &&
        index === self.findIndex((o) => o.value === opt.value)
    );

  const lrOptions = (selectedLegalMain || []).map((lr) => ({
    value: lr?.lr_id ?? lr?.id ?? lr?.requirement_id,
    label: lr?.req_number || lr?.number || lr?.description || String(lr?.lr_id || lr?.id),
  }));

  const handleClose = () => {
    setSelectedEEs([]);
    setSelectedLegals([]);
    onClose();
  };

  const extractKeywordIds = (selectedEEsList, selectedEEMainList) => {
    const keywords = [];
    for (const ee of selectedEEsList) {
      const foundItem = selectedEEMainList.find(
        (item) =>
          (item.ee_id || item.swm_id || item.vs_id || item.id) === ee.value
      );
      if (
        foundItem?.keywords &&
        Array.isArray(foundItem.keywords) &&
        foundItem.keywords.length > 0
      ) {
        for (const kw of foundItem.keywords) {
          const kwId = kw?.keyword_id ?? kw?.id ?? kw?.value ?? kw;
          if (kwId) {
            keywords.push(Number(kwId));
          }
        }
      } else if (ee?.value !== undefined && ee?.value !== null && ee?.value !== "") {
        keywords.push(Number(ee.value));
      }
    }
    return [...new Set(keywords)];
  };

  const handleSubmit = async () => {
    let success = true;
    const enrichedTestCases = [];
    const keywordsFromSelectedComponents = showKeywordSelect
      ? extractKeywordIds(selectedEEs, selectedEEMain || [])
      : [];

    for (const tc of testCasesToLink || []) {
      const payload = {
        test_case_id: tc?.test_case_id?.toString(),
        description: tc.description,
        status: tc.status || "in-progress",
        ...(showKeywordSelect && {
          component_keyword_to_link: {
            entity_type: entityType,
            keyword_ids: keywordsFromSelectedComponents,
          },
        }),
        ...(showLegalSelect && {
          requirement_ids: selectedLegals.map((lr) => Number(lr.value)),
        }),
      };

      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...testCasesActions.update_test_case,
            params: payload,
            show_toast: true,
          })
        )
      );

      if (resp?.error) {
        success = false;
        break;
      }

      const newTestCaseId = resp?.data?.test_case_id;
      enrichedTestCases.push({
        ...tc,
        ...(showKeywordSelect ? { is_component_linked: true } : {}),
        ...(showLegalSelect ? { is_lr_linked: true } : {}),
        ...(newTestCaseId ? { new_test_case_id: newTestCaseId } : {}),
      });
    }

    if (success) {
      onSuccess(enrichedTestCases);
      handleClose();
    }
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={handleClose}
      title="Link Test Case Component"
      successButtonName="Submit"
      rejectButtonName="Close"
      onClickSuccess={handleSubmit}
      onDisabled={isSubmitDisabled}
      actionButtonStyle="continuous"
      onClickReject={handleClose}
    >
      <Stack spacing={2} mb={2} sx={{ minWidth: "290px", maxWidth: "400px" }}>
        {showKeywordSelect && (
          <CustomMultiSelectV2
            label="Select keywords"
            required={true}
            optionsList={eeOptions}
            selectedValues={selectedEEs}
            setSelectedValues={setSelectedEEs}
          />
        )}
        {showLegalSelect && (
          <CustomMultiSelectV2
            label="Select Legal Requirements"
            required={true}
            optionsList={lrOptions}
            selectedValues={selectedLegals}
            setSelectedValues={setSelectedLegals}
          />
        )}
      </Stack>
    </CustomDialogBoxV2>
  );
}

TestCaseComponentLinkDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  testCasesToLink: PropTypes.array,
  selectedEEMain: PropTypes.array,
  selectedLegalMain: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  entityType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default TestCaseComponentLinkDialog;
